import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { countries } from '../../../data/countries';
import { getSupplierById, updateSupplier } from '../services/supplierService';

const emptyForm = {
  name: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  country: '',
  taxNumber: '',
  notes: ''
};

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } };

const EditSupplier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        const response = await getSupplierById(id);
        const supplier = response?.payload || response?.data || response;
        setFormData({
          name: supplier?.Name || supplier?.SupplierName || '',
          contactPerson: supplier?.Contactperson || supplier?.ContactPerson || '',
          email: supplier?.Email || '',
          phone: supplier?.MobileNumber || supplier?.Phone || '',
          address: supplier?.Street || supplier?.Address || '',
          postalCode: supplier?.PostCode || supplier?.PostalCode || '',
          city: supplier?.City || '',
          country: supplier?.Country || '',
          taxNumber: supplier?.BTWNumber || supplier?.TaxNumber || '',
          notes: supplier?.Notes || ''
        });
      } catch (fetchError) {
        setError(fetchError.response?.data?.message || t('supplier_not_found') || 'Supplier could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    fetchSupplier();
  }, [id, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'notes' && value.length > 500) return;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = t('supplier_name_required');
    if (formData.name.length > 100) nextErrors.name = t('supplier_name_max_length');
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) nextErrors.email = t('email_invalid');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error(!formData.name.trim() ? t('please_fill_required_field') : t('please_fix_form_errors'));
      return;
    }

    setSaving(true);
    try {
      await updateSupplier(id, {
        Name: formData.name.trim(),
        Contactperson: formData.contactPerson.trim() || null,
        Email: formData.email.trim() || null,
        BTWNumber: formData.taxNumber.trim() || null,
        Street: formData.address.trim() || null,
        StreetNumber: null,
        PostCode: formData.postalCode.trim() || null,
        City: formData.city.trim() || null,
        Country: formData.country.trim() || null,
        MobileNumber: formData.phone.trim() || null,
        Notes: formData.notes.trim() || null
      });
      toast.success(t('supplier_updated_successfully') || 'Supplier updated successfully!');
      navigate('/suppliers');
    } catch (submitError) {
      toast.error(submitError.response?.data?.message || t('supplier_update_failed') || 'Failed to update supplier.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: 4, px: { xs: 1, sm: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
          {t('edit_supplier') || 'Edit Supplier'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('manage_suppliers_desc') || 'Manage supplier information'}
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                {t('basic_information') || 'Basic information'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}><TextField fullWidth required label={`${t('supplier_name') || 'Supplier name'} *`} name="name" value={formData.name} onChange={handleChange} error={Boolean(errors.name)} helperText={errors.name || 'Max 100 characters'} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label={t('contact_person') || 'Contact person'} name="contactPerson" value={formData.contactPerson} onChange={handleChange} helperText="Optional" disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="email" label={t('email_address') || 'Email'} name="email" value={formData.email} onChange={handleChange} error={Boolean(errors.email)} helperText={errors.email || 'Optional'} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label={t('phone_number') || 'Phone'} name="phone" value={formData.phone} onChange={handleChange} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label={t('vat_number') || 'VAT number'} name="taxNumber" value={formData.taxNumber} onChange={handleChange} placeholder="BE0123456789" disabled={saving} sx={fieldSx} /></Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                {t('address_information') || 'Address information'}
              </Typography>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label={t('address') || 'Address'} name="address" value={formData.address} onChange={handleChange} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label={t('city') || 'City'} name="city" value={formData.city} onChange={handleChange} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label={t('postal_code') || 'Postal code'} name="postalCode" value={formData.postalCode} onChange={handleChange} disabled={saving} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth select label={t('country') || 'Country'} name="country" value={formData.country} onChange={handleChange} disabled={saving} sx={fieldSx}><MenuItem value=""><em>{t('select_country') || 'Select country'}</em></MenuItem>{countries.map((country) => <MenuItem key={country.code || country.name} value={country.name}>{country.name}</MenuItem>)}</TextField></Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                {t('notes') || 'Notes'}
              </Typography>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={4} label={t('notes') || 'Notes'} name="notes" value={formData.notes} onChange={handleChange} error={Boolean(errors.notes)} helperText={errors.notes || `${formData.notes.length}/500`} disabled={saving} sx={fieldSx} /></Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, mt: 1 }}>
              <Button variant="outlined" onClick={() => navigate('/suppliers')} disabled={saving} sx={{ width: { xs: '100%', sm: 'auto' } }}>{t('cancel') || 'Cancel'}</Button>
              <Button type="submit" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                {saving ? (t('saving') || 'Saving...') : (t('update_supplier') || 'Update supplier')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditSupplier;
